import { clearTenantScopedState } from '../lib/tenantSession';
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      /**
       * CRM AUTH (Firebase) — PRIMARY SOURCE
       * Stored by CRMAuthContext after OTP verification
       */
      const crmUser = localStorage.getItem("crm_user_data");
      const crmToken = localStorage.getItem("crm_access_token");

      if (crmUser && crmToken) {
        const parsed = JSON.parse(crmUser);

        setUser({
          ...parsed,
          role: parsed.role || "customer",
        });

        setIsAuthenticated(true);
        setLoading(false);
        return;
      }

      /**
       * LEGACY / BUSINESS USERS (if any)
       */
      const savedUser = localStorage.getItem("user");
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        // Only trust the legacy key if it has the minimum required fields.
        // Stale entries from a previous CRM session (missing role/id) are discarded.
        if (parsedUser && (parsedUser.role || parsedUser.id || parsedUser.email)) {
          setUser(parsedUser);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem("user");
        }
      }
    } catch (e) {
      localStorage.removeItem("crm_user_data");
      localStorage.removeItem("crm_access_token");
      localStorage.removeItem("user");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login here ONLY sets local state
   * Authentication already happened elsewhere (Firebase or internal flow)
   */
  const login = async (userData) => {
    if (!userData) return;

    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);

    localStorage.removeItem("crm_user_data");
    localStorage.removeItem("crm_access_token");
    localStorage.removeItem("user");
    // PMT1: the selected tenant context is part of the session — it must not outlive it, or the next
    // sign-in on this browser could inherit the previous person's business.
    clearTenantScopedState();
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, loading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
